import "./ButtonInstance.css";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  documentPrompts,
  sampleSelectedTextActions,
  defaultAssistantsPrompts,
} from "../helpers/prompts";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules"; // Import Navigation module
import "swiper/swiper-bundle.css";
import SuggestedPrompts from "./SuggestedPrompts.js";
import { IconButton, Tooltip } from "@mui/material";
import { AttachmentContainer } from "./AttachmentContainer.js";
import { getRemovedChatDocKeys, toAttachmentFiles } from "../helpers/chatDocs.js";
import Utils from "../utils/utils.js";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import StopIcon from "@mui/icons-material/Stop";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ChatAssistantDropDown2 from "../pages/ChatAssistantDropdown2";
import UploadEverywhere from "./UploadEverywhere.js";
import ChatAnnouncement from "./ChatAnnouncement";
import GlobalVariables from "../models/globalVariables";
import {
  getWhiteLabeledAssistantName,
  truncateAssistantName,
} from "../helpers/whiteLabelAssistantName.js";
import { isDocumentPortalMode } from "../helpers/chatWithDocsCapability.js";

export const RESPONSE_IN_PROGRESS_MESSAGE =
  "Addy is still answering. Wait for it to finish or press Stop first.";

// Opening the + menu moves focus to its first item for keyboard users. Only
// highlight that focus when it came from the keyboard, so after a mouse click
// "Chat with your docs" does not look pre-selected.
const isKeyboardFocus = (element) => {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true;
  }
};

const FOLLOW_UP_SKELETON_IDS = [1, 2, 3];

const MAX_CHAT_FILES = 20;
const FILE_LIMIT_MESSAGE = `You can only upload up to ${MAX_CHAT_FILES} files per chat.`;

const ButtonInstance = (props) => {
  const isChatWithDocsEnabled = props.chatWithDocsEnabled !== false;
  const isCompareGuidelinesEnabled =
    !isDocumentPortalMode(props.mode) &&
    props.compareGuidelinesEnabled !== false &&
    !!props.onCompareModeChange;
  const attachmentMenuLabel = isChatWithDocsEnabled
    ? "Attach or chat with docs"
    : "Attach files";
  const [textareaValue, setTextareaValue] = useState("");
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [selectedType, setSelectedType] = useState(
    "What do you want to do with this text?"
  );
  const [linesCount, setLinesCount] = useState(0);
  const [viewTitle, setViewTitle] = useState("Selected text");
  const [maxCharsPerLine, setMaxCharsPerLine] = useState(30);
  const [selectedTextActions, setSelectedTextActions] = useState(
    sampleSelectedTextActions
  );
  const [showAssistantDropdown, setShowAssistantDropdown] = useState(false);
  const assistantDropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  // Animated placeholder state
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Portal state for dropdown positioning
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // New state for dynamic suggested prompts
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Portal state for guideline dropdown positioning
  const [guidelineDropdownPosition, setGuidelineDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // State for drag and drop functionality
  const [isDragActive, setIsDragActive] = useState(false);

  // Plus button menu state
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const plusButtonRef = useRef(null);
  const plusMenuRef = useRef(null);
  const [plusMenuPosition, setPlusMenuPosition] = useState({ bottom: 0, left: 0 });

  // Helper function to check if all files/attachments are ready
  const areAllFilesReady = () => {
    // If using selectedAttachment, it's always ready (no loading state)
    if (props.selectedAttachment) {
      return true;
    }
    // If using props.files, check that all files are not loading
    if (props?.files) {
      return props.files.every((file) => !file?.loading);
    }
    // If no files or attachments, consider it ready
    return true;
  };
  const inputDisabled = props.inputDisabled;
  const queuedExternalQueries = Array.isArray(props.queuedExternalQueries)
    ? props.queuedExternalQueries
    : [];
  const nextQueuedExternalQuery = queuedExternalQueries[0] || null;

  // Helper function to check if the button should be enabled
  const isButtonEnabled = () => {
    // If using selectedAttachment, enable button even without selectedAssistant
    if (inputDisabled) {
      return false;
    }
    if (props.selectedAttachment) {
      return textareaValue && !currentlyTyping && areAllFilesReady();
    }
    // For regular files, require selectedAssistant
    return props.selectedAssistant && textareaValue && !currentlyTyping && areAllFilesReady();
  };
  const [isPositionCalculated, setIsPositionCalculated] = useState(false);
  const guidelineDropdownRef = useRef(null);

  useEffect(() => {
  }, [showAssistantDropdown]);
  
  // Placeholder array
  let placeholders = [
    "Ask me anything...", // Important
    "Process loans in minutes!",
    "Calculate any type of income",
    "Attach any document to review",
    "Search guidelines",
    "Get a live rate quote",
    "Summarize AUS findings",
    "Ask me anything...",
    "Ready when you are!"
  ];

  if (props.mode === "documentPortal") {
    placeholders = [
      "Which documents still need to be uploaded?",
      "What's the status of my submitted documents?",
      "Are there any items that need corrections?",
      "What should I do next to move things forward?",
      "Which of my documents have been approved?",
      "Ask me anything about your loan...",
    ];
  } else if (props.mode === "helpCenter") {
    placeholders = [
      "How do I get started with Addy?",
      "How do I install the Chrome extension?",
      "How do I upload and process documents?",
      "How do AI Agent Tasks work?",
      "How do I invite my team?",
      "Ask me anything about the Help Center...",
    ];
  } else if (props.overlayMode) {
    placeholders = [
      "Ask me anything...", // Important
      "How many loans are in my pipeline?",
      "Calculate any type of income",
      "Attach any document to review",
      "Search guidelines",
      "What's the minimum credit score for Fannie Mae?",
      "Get a live rate quote",
      "How many loans have lock expirations?",
      "What are today's rates?",
      "Get a loan estimate",
      "Analyze my loan pipeline",
      "How can we close more loans?",
      "Beat a loan estimate",
      "Ask me anything...",
      "Ready when you are!",
    ]
  }

  // Loan-context role suggestions (Credit / Income / Assets) replace the
  // generic action buttons when the parent reports a supported role + loan.
  const loanRoleSuggestions = Array.isArray(props.loanRoleSuggestions)
    ? props.loanRoleSuggestions
    : [];

  // Static action buttons for dynamic suggested prompts
  const defaultStaticActionButtons = [
    {
      id: 'guideline',
      label: 'Ask a guideline question',
      hasDropdown: true,
      svgIconPath: "/suggested_ask_question.svg",
      prompts: [
        "What's the minimum credit score for Fannie Mae?",
        "What are the DTI requirements for conventional loans?",
        "How do I calculate self-employed income?",
        "What documents are needed for a refinance?",
        "What are today's rates?",
        "What's the maximum loan amount for conventional?",
        "How do I verify employment for a loan?",
        "What are the seasoning requirements for cash-out?"
      ]
    },
    {
      id: 'price',
      label: 'Price a loan',
      hasDropdown: true,
      svgIconPath: "/suggested_price_a_loan.svg",
      prompts: [
        "Price a loan for a one-unit detached single family home in my area",
        "What's the current rate for a 30 year fixed loan in my area?",
        "What are the current market rates",
        "Rate for a $500k property in my area with a 680 credit score borrower",
      ]
    },
    {
      id: 'income',
      label: 'Calculate income',
      hasDropdown: true,
      svgIconPath: "/suggested_calculate_income.svg",
      prompts: [
        "Calculate Self-Employed income per Fannie Mae guidelines",
        "Calculate W-2 income per Fannie Mae guidelines",
        "Calculate W-2 income per Freddie Mac guidelines",
        "Calculate Self-Employed income per Freddie Mac guidelines",
        "I have a borrower $500 monthly social security income, calculate their qualified income?",
        "My borrower has a $1000 monthly alimony income, calculate their qualified income?",
      ]
    },
    {
      id: 'pipeline',
      label: 'Pipeline review',
      hasDropdown: true,
      svgIconPath: "/suggested_pipeline_review.svg",
      prompts: [
        "How many loans are in my pipeline?",
        "What's the total amount of loans in my pipeline?",
        "How many loans have lock expirations soon?",
        "Find me a loan for a specific borrower",
        "Which documents are available for a specific borrower?",
      ]
    },
    {
      id: 'beat',
      label: 'Beat a loan estimate',
      hasDropdown: true,
      svgIconPath: "/suggested_beat_loan_estimate.svg",
      prompts: [
        "Beat this loan estimate with a better rate",
        "Beat this loan estimate with a better monthly payment",
        "Give me options to beat this loan estimate",
      ]
    }
  ];

  // Help Center action pills. When embedded in the Addy Help Center
  // (mode=helpCenter) the loan-workflow pills (Price a loan, Pipeline review,
  // etc.) don't apply, so we surface documentation-oriented questions grouped by
  // the Help Center article categories instead.
  const helpCenterActionButtons = [
    {
      id: 'getting-started',
      label: 'Getting started',
      hasDropdown: true,
      svgIconPath: "/suggested_ask_question.svg",
      prompts: [
        "How do I get started with Addy AI?",
        "How do I install the Chrome extension?",
        "How do I navigate my loan pipeline view?",
        "How do I set up my account?",
      ]
    },
    {
      id: 'documents',
      label: 'Documents',
      hasDropdown: true,
      svgIconPath: "/suggested_pipeline_review.svg",
      prompts: [
        "How do I upload documents in Addy?",
        "How does Addy process and classify documents?",
        "Where can I see the status of my documents?",
        "What document types does Addy support?",
      ]
    },
    {
      id: 'ai-agents',
      label: 'AI agents & tasks',
      hasDropdown: true,
      svgIconPath: "/suggested_calculate_income.svg",
      prompts: [
        "What are AI Agent Tasks and how do they work?",
        "How do I create and configure an AI agent?",
        "How do I train my AI assistant?",
        "How do I use Ask Addy AI?",
      ]
    },
    {
      id: 'team',
      label: 'Team & organization',
      hasDropdown: true,
      svgIconPath: "/suggested_beat_loan_estimate.svg",
      prompts: [
        "How do I invite team members to my organization?",
        "How do I manage roles and permissions?",
        "How do I manage my organization settings?",
      ]
    },
    {
      id: 'integrations-billing',
      label: 'Integrations & billing',
      hasDropdown: true,
      svgIconPath: "/suggested_price_a_loan.svg",
      prompts: [
        "How do I connect an integration?",
        "Which integrations does Addy support?",
        "How do I manage billing and my subscription?",
        "How do I upgrade my plan?",
      ]
    },
  ];

  // Short, article-based questions for the empty-state carousel when running in
  // the Help Center. Kept separate from the action pills above so the carousel
  // stays concise.
  const helpCenterSuggestedPrompts = [
    "How do I get started with Addy?",
    "How do I install the Chrome extension?",
    "How do I upload and manage documents?",
    "How do AI Agent Tasks work?",
    "How do I train my AI assistant?",
    "How do I invite team members?",
    "How do I connect an integration?",
    "How do I manage billing?",
  ];

  // When the parent provides loan-role suggestions we render those exclusively
  // (replacing the generic empty-state pills), otherwise fall back to defaults.
  // Help Center mode always uses the documentation-oriented pills.
  const staticActionButtons = props.mode === "helpCenter"
    ? helpCenterActionButtons
    : loanRoleSuggestions.length > 0
      ? loanRoleSuggestions.map((btn) => ({ hasDropdown: true, ...btn }))
      : defaultStaticActionButtons;

  const urlParams = new URLSearchParams(window.location.search);
  const clientHostname = urlParams.get("clientHostname");

  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const singleLineContentHeightRef = useRef(null);
  const clientID = props.clientID;
  const data = props.data;
  const currentlyTyping = props.currentlyTyping;
  const numberOfMessagesInChat = props.numberOfMessagesInChat;
  const followUpSuggestions = Array.isArray(props.followUpSuggestions)
    ? props.followUpSuggestions
    : [];
  const shouldShowFollowUps =
    numberOfMessagesInChat > 0 &&
    !currentlyTyping &&
    (props.isGeneratingFollowUps || followUpSuggestions.length > 0);
  const shouldShowFollowUpSkeletons =
    props.isGeneratingFollowUps && followUpSuggestions.length === 0;
  const currentView = props.currentView;
  const uid = props.uid;
  const isDocumentChat = props.isDocumentChat;
  const compareMode = props.compareMode;
  const overlayMode = props.overlayMode;
  const brandedAssistantName =
    typeof props.assistantName === "string" ? props.assistantName.trim() : "";
  const assistantDisplayName =
    getWhiteLabeledAssistantName(props.selectedAssistant, props.assistantName) ||
    brandedAssistantName;
  const getAssistantHelpTitle = (fallbackTitle) => {
    if (!brandedAssistantName) return fallbackTitle;
    const suffix = fallbackTitle.includes("today") ? " help you today?" : " help?";
    return `How can ${brandedAssistantName}${suffix}`;
  };

  let titleMessage;
  let descriptionMessage;
  if (clientHostname && !clientHostname.includes("addy")) {
    // If the client hostname exists (meaning this is embeded in another) website
    // for instance chrome extension and hostname is not addy
    titleMessage = getAssistantHelpTitle("How can I help?");
    descriptionMessage = "";
    if (
      clientHostname === "app.heynouri.ai" ||
      clientHostname === "nourimortgagedev.addy.so"
    ) {
      titleMessage = getAssistantHelpTitle("How can I help you today?"); // Was previously search lending guidelines
      descriptionMessage =
        "I can answer questions about mortgage lending guidelines, loan programs, and help analyze documents";
    }
  } else {
    const isAddyDefaultOrV2 =
      props.selectedAssistant?.appID ===
        "addy-default-assistant-c66e0609-371e-49b2-bb4a-5186526b2a9d" ||
      props.selectedAssistant?.appID ===
        "addy-default-assistant-c66e0609-371e-49b2-bb4a-5186526b2a9d-v2";
    titleMessage = isAddyDefaultOrV2
      ? getAssistantHelpTitle("How can I help you today?") // Was previously search lending guidelines
      : getAssistantHelpTitle("How can I help you today?");
    descriptionMessage = isAddyDefaultOrV2
      ? "I can answer questions about mortgage lending guidelines, loan programs, and help analyze documents"
      : `I can answer questions from the ${assistantDisplayName || ""} assistant knowledge base`;
  }

  const [selectedText, setSelectedText] = useState(
    data?.action === "selectedText" && data?.text ? data.text : ""
  );

  // eslint-disable-next-line no-undef
  const chatInfo = document.querySelector("#addy-chat-info");
  if (selectedText && selectedText.length && chatInfo) {
    chatInfo.remove();
  }

  // When the component loads for the first time, once, make the cursor
  // focus on the textarea
  useEffect(() => {
    const textarea = document.querySelector(".addy-prompt-input");
    if (textarea) {
      textarea.focus();
    }
  }, []);

  // In Help Center mode we always show the article-based suggested prompts and
  // skip the assistant-derived ones below.
  useEffect(() => {
    if (props.mode === "helpCenter") {
      setSuggestedPrompts(
        [...helpCenterSuggestedPrompts]
          .sort(() => 0.5 - Math.random())
          .slice(0, 3)
      );
    }
  }, [props.mode]);

  useEffect(() => {
    if (props.mode === "helpCenter") return;
    // console.log("props.selectedAssistant", props.selectedAssistant);
    if (
      props.selectedAssistant &&
      props?.selectedAssistant?.suggestedQuestions
    ) {
      // select random 3 if there are more than 3
      const randomPrompts2 = props?.selectedAssistant?.suggestedQuestions
        .sort(() => 0.5 - Math.random())
        .slice(0, 3);
      setSuggestedPrompts(randomPrompts2);
    } else {
      const promptKey =
        props?.selectedAssistant?.defaultId || "addy-assistant-001";
      const promptsArray =
        defaultAssistantsPrompts[promptKey] ??
        defaultAssistantsPrompts["addy-assistant-001"] ??
        [];
      const randomPrompts2 = Array.isArray(promptsArray)
        ? [...promptsArray].sort(() => 0.5 - Math.random()).slice(0, 3)
        : [];
      setSuggestedPrompts(randomPrompts2);
    }
  }, [props.selectedAssistant]);

  const setButtonInstancePosition = (numMessages, currentView) => {
    let alignInputBottom = overlayMode || props?.alignInputBottom;
    if (overlayMode) alignInputBottom = false; // Align input to top right now
    const buttonInstance = document.querySelector(".button-instance");
    if (!buttonInstance) return;
    if (
      numMessages === 0 &&
      currentView === "select-assistant" &&
      !selectedText
    ) {
      if (alignInputBottom) {
        buttonInstance.style.top = "auto";
        buttonInstance.style.transform = "none";
        buttonInstance.style.bottom = "20px";
      } else {
        // Keep the initial empty-state view centered regardless of viewport size.
        buttonInstance.style.top = "50%";
        buttonInstance.style.transform = "translateY(-50%)";
        buttonInstance.style.bottom = "auto";
      }
    } else {
      buttonInstance.style.top = "auto";
      buttonInstance.style.transform = "none";
      buttonInstance.style.bottom = "20px";
    }
  };

  const toggleCompareMode = async (compareModeT) => {
    const newCompareMode = !compareModeT;
    await props.onCompareModeChange(newCompareMode);
  };

  // When number of messages in chat changes, set button instance margin bottom
  useEffect(() => {
    setButtonInstancePosition(numberOfMessagesInChat, currentView);
  }, [numberOfMessagesInChat, currentView, selectedText]);

  // Keep cursor and latest line visible when multiline content grows
  useEffect(() => {
    const el = textareaRef.current;
    if (el && textareaValue) {
      el.scrollTop = el.scrollHeight - el.clientHeight;
    }
  }, [textareaValue, linesCount]);

  // Animated placeholder logic
  useEffect(() => {
    let shouldAnimate = props.animatedPlaceholders !== undefined 
      ? props.animatedPlaceholders 
      : !props.overlayMode;

    if (props.numberOfMessagesInChat > 0) {
      shouldAnimate = false;
      // set placeholder index to 0
      setCurrentPlaceholderIndex(0);
    }

    if (!shouldAnimate) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      
      setTimeout(() => {
        setCurrentPlaceholderIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % placeholders.length;
          return newIndex;
        });
        setIsAnimating(false);
      }, 150); // Half of transition time for fade out
      
    }, 4000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [props.animatedPlaceholders, props.overlayMode, props.numberOfMessagesInChat]);

  // Not gated on clientID: the web app passes it inside `data`, so it is null there.
  useEffect(() => {
    const handleSelectedTextFromHostPage = (event) => {
      if (event.source !== window.parent || event.data?.key !== "selectedText") {
        return;
      }

      setSelectedText(typeof event.data.value === "string" ? event.data.value : "");
    };
    window.addEventListener("message", handleSelectedTextFromHostPage);

    return () => {
      window.removeEventListener("message", handleSelectedTextFromHostPage);
    };
  }, []);

  useEffect(() => {
    // Set suggested prompts to be 10 random prompts from samplePrompts.
    // Help Center mode manages its own prompts in a dedicated effect above.
    if (props.mode === "helpCenter") {
      // no-op: prompts handled by the helpCenter effect
    } else if (props?.selectedAssistant) {
      if (props?.selectedAssistant?.suggestedQuestions) {
        // select random 3 if there are more than 3
        const randomPrompts2 = props?.selectedAssistant?.suggestedQuestions
          .sort(() => 0.5 - Math.random())
          .slice(0, 3);
        setSuggestedPrompts(randomPrompts2);
      } else {
        const promptKey =
          props?.selectedAssistant?.defaultId || "addy-assistant-001";
        const promptsArray =
          defaultAssistantsPrompts[promptKey] ??
          defaultAssistantsPrompts["addy-assistant-001"] ??
          [];
        const randomPrompts2 = Array.isArray(promptsArray)
          ? [...promptsArray].sort(() => 0.5 - Math.random()).slice(0, 3)
          : [];
        setSuggestedPrompts(randomPrompts2);
      }
    } else {
      const promptKey =
        props?.selectedAssistant?.defaultId || "addy-assistant-001";
      const promptsArray =
        defaultAssistantsPrompts[promptKey] ??
        defaultAssistantsPrompts["addy-assistant-001"] ??
        [];
      const randomPrompts2 = Array.isArray(promptsArray)
        ? [...promptsArray].sort(() => 0.5 - Math.random()).slice(0, 3)
        : [];
      setSuggestedPrompts(randomPrompts2);
    }

    if (data && data["action"] === "selectedText") {
      setSelectedText(data["text"]);
    }
  }, [clientID]); // Run this effect when clientID changes

  useEffect(() => {
    // remove chat info onboarding text if selected text is present
    if (selectedText && selectedText.length && chatInfo) {
      chatInfo.remove();
    }
  }, [selectedText]); // Reload component on selected text change

  // Reload component on currentlyTyping change
  useEffect(() => {}, [currentlyTyping]);

  // Reload when props change
  useEffect(() => {
    if (
      props.currentEmailAction === "Analyze attachment" &&
      props.selectedAttachment
    ) {
      setSelectedText(props.selectedAttachment.name || "");
      setSelectedType("More ideas for you");
      setViewTitle("Chat with your document");
      if (
        props.selectedAttachment.tag &&
        documentPrompts[props.selectedAttachment.tag]?.length
      ) {
        setSelectedTextActions(documentPrompts[props.selectedAttachment.tag]);
      } else {
        setSelectedTextActions([
          "Extract text",
          "Translate",
          "Write a blog post about this document",
        ]);
      }
      // get the analyze attachment parent indicator
      // const analyzeAttachmentParent = document.querySelector(".selected-text-parent");
      // if (analyzeAttachmentParent) {
      //     analyzeAttachmentParent.classList.add("analyze-attachment-parent");
      // }
    } else if (props.currentEmailAction === "Analyze document") {
      setSelectedText(props.selectedDocument?.name || "My document.pdf");
      setSelectedType("More ideas for you");
      setViewTitle("Chat with your document");
      if (
        props.selectedDocument?.tag &&
        documentPrompts[props.selectedDocument?.tag]?.length
      ) {
        setSelectedTextActions(documentPrompts[props.selectedDocument.tag]);
      } else {
        setSelectedTextActions([
          "Extract text",
          "Translate",
          "Write a blog post about this document",
        ]);
      }
    } else if (
      props.currentEmailAction === "Other files" &&
      props.selectedAttachment
    ) {
      setSelectedText(props.selectedAttachment.name || "");
      setSelectedType("More ideas for you");
      setViewTitle("Chat with your file");
    } else {
      if (selectedText) {
        setSelectedType("What do you want to do with this text?");
        setViewTitle("Selected text");
        setSelectedTextActions(sampleSelectedTextActions);
      }
    }
    if (props.maxCharsPerLine) {
      setMaxCharsPerLine(props.maxCharsPerLine);
    }
  }, [props]);

  // Handle clicks outside dropdown
  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutsideDropdown);
      return () => document.removeEventListener('click', handleClickOutsideDropdown);
    }
  }, [isDropdownOpen]);

  // Reload when linesCount changes
  useEffect(() => {}, [linesCount]);

  // Handle click outside for assistant dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        assistantDropdownRef.current &&
        !assistantDropdownRef.current.contains(event.target)
      ) {
        //console.log("handleClickOutside setShowAssistantDropdown to false");
        setShowAssistantDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Handle change event of textarea – use new value for line count so it’s not stale
  const handleChange = (event) => {
    if (inputDisabled) return;
    const newValue = event.target.value;
    const textarea = event.target;
    setTextareaValue(newValue);
    const lineBreakCount = newValue.split(/\r\n|\r|\n/).length;
    const computedStyle = window.getComputedStyle(textarea);
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const contentHeight = Math.max(0, textarea.scrollHeight - paddingTop - paddingBottom);
    if (!singleLineContentHeightRef.current || newValue.length === 0) {
      singleLineContentHeightRef.current = Math.max(contentHeight, 1);
    }
    const singleLineHeight = singleLineContentHeightRef.current || Math.max(contentHeight, 1);
    const visualLineCount = Math.max(1, Math.ceil(contentHeight / singleLineHeight));
    const count =
      newValue === ""
        ? 1
        : Math.max(lineBreakCount, visualLineCount);
    setLinesCount(count <= 5 ? count : 5);
  };

  // Handle key down event of textarea
  const handleKeyDown = (event) => {
    if (inputDisabled) {
      event.preventDefault();
      return;
    }
    if (event.key === "Enter" && (event.shiftKey || event.altKey)) {
      // If Shift+Enter or Alt+Enter is pressed, create a new line
      event.stopPropagation();
      if (linesCount < 5) {
        setTextareaValue((prevValue) => prevValue + "\n");
      }
    } else if (event.key === "Enter") {
      onClick(textareaValue, selectedText, compareMode);
      event.preventDefault();
    }
  };

  const showResponseInProgressToast = () => {
    props.toastify?.(RESPONSE_IN_PROGRESS_MESSAGE, false, true);
  };

  const onClick = () => {
    if (!textareaValue || inputDisabled) return;
    if (currentlyTyping) {
      showResponseInProgressToast();

      return;
    }
    props.onClick(
      textareaValue,
      selectedText,
      compareMode,
      compareMode ? "true" : "false",
      props.files
    );
    setTextareaValue("");
    setLinesCount(1);
    // Reset the selected text
    setSelectedText("");
  };

  const onSuggestedPromptClick = (promptOrItem) => {
    if (inputDisabled) return;
    if (currentlyTyping) {
      showResponseInProgressToast();

      return;
    }
    // Support either a plain string prompt (legacy) or an object
    // { label, prompt } where the label is the visible chat-bubble text
    // and the prompt is the full instruction sent to the model.
    const isStructured =
      promptOrItem &&
      typeof promptOrItem === "object" &&
      typeof promptOrItem.prompt === "string";
    const promptText = isStructured ? promptOrItem.prompt : promptOrItem;
    const displayPromptOverride = isStructured ? promptOrItem.label : null;
    // Reset the selected text
    props.onClick(
      promptText,
      selectedText,
      compareMode,
      compareMode ? "true" : "false",
      undefined,
      displayPromptOverride
    );
    setSelectedText("");
  };
  const onSelectedTextClose = async () => {
    if (isDocumentChat) {
      await props.onCloseAttachment();
    }
    setSelectedText("");
  };

  const closeHotTip = () => {
    const hotTipDiv = document.getElementById("hot-tip-div");
  };

  const suggestedPromptsCarousel = (prompts) => {
    return (
      <SuggestedPrompts
        prompts={prompts}
        onClick={(prompt) => onSuggestedPromptClick(prompt)}
      />
    );
  };

  const handleFileChange = async (e) => {
    if (inputDisabled) return;
    const acceptedFiles = Array.from(e.target.files);

    acceptedFiles.forEach((file) => {
      const id = `${file.name}-${Date.now()}`;
      const preview = URL.createObjectURL(file);

      // Immediately add to state as "loading"
      const tempFile = {
        id,
        name: file.name,
        type: file.type,
        preview,
        loading: true,
      };
      props.setFiles((prevFiles) => [...prevFiles, tempFile]);

      const reader = new FileReader();

      reader.onloadend = async () => {
        const base64 = reader.result;

        // Check for PDF header (25504446)
        const arr = new Uint8Array(
          atob(base64.split(",")[1])
            .split("")
            .map((char) => char.charCodeAt(0))
        ).subarray(0, 4);

        let header = "";
        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16);
        }

        if (header === "25504446") {
          file = new File([file], file.name, { type: "application/pdf" });
        }

        // Now do backend call
        const response = await Utils.base64ToText(
          base64,
          file.type.split("/")[1]
        );

        if (response.success) {
          if (response.textContent?.length === 0) {
            props.toastify("There was no text found in the file.", false, true);
          }
          props.setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.id === id
                ? {
                    ...f,
                    loading: false,
                    base64,
                    textContent: response.textContent,
                  }
                : f
            )
          );
        } else {
          // Remove file from UI on failure
          props.setFiles((prevFiles) => prevFiles.filter((f) => f.id !== id));
          props.toastify(
            "Error while processing file, please try another file.",
            false
          );
        }
      };

      reader.readAsDataURL(file);
    });
  };

  const isOverChatFileLimit = (newFileCount) => {
    const sentFileCount = (props.usedFiles || []).flatMap(
      (sentMessage) => sentMessage.files || []
    ).length;

    return (
      (props.files?.length || 0) + newFileCount + sentFileCount > MAX_CHAT_FILES
    );
  };

  const handleFiles = () => {
    if (inputDisabled) return;
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  // Handle drag and drop file upload
  const handleDragDropUpload = async (files) => {
    // Check file limit before processing
    if (inputDisabled) return;
    if (isOverChatFileLimit(files.length)) {
      props.toastify(FILE_LIMIT_MESSAGE, false);

      return;
    }

    // Process each file using the existing handleFileChange logic
    files.forEach((file) => {
      const id = `${file.name}-${Date.now()}`;
      const preview = URL.createObjectURL(file);

      // Immediately add to state as "loading"
      const tempFile = {
        id,
        name: file.name,
        type: file.type,
        preview,
        loading: true,
      };
      props.setFiles((prevFiles) => [...prevFiles, tempFile]);

      const reader = new FileReader();

      reader.onloadend = async () => {
        const base64 = reader.result;

        // Check for PDF header (25504446)
        const arr = new Uint8Array(
          atob(base64.split(",")[1])
            .split("")
            .map((char) => char.charCodeAt(0))
        ).subarray(0, 4);

        let header = "";
        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16);
        }

        if (header === "25504446") {
          file = new File([file], file.name, { type: "application/pdf" });
        }

        // Now do backend call
        const response = await Utils.base64ToText(
          base64,
          file.type.split("/")[1]
        );

        if (response.success) {
          if (response.textContent?.length === 0) {
            props.toastify("There was no text found in the file.", false, true);
          }
          props.setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.id === id
                ? {
                    ...f,
                    loading: false,
                    base64,
                    textContent: response.textContent,
                  }
                : f
            )
          );
        } else {
          // Remove file from UI on failure
          props.setFiles((prevFiles) => prevFiles.filter((f) => f.id !== id));
          props.toastify(
            "Error while processing file, please try another file.",
            false
          );
        }
      };

      reader.readAsDataURL(file);
    });
  };

  // Handle drag start/end for visual feedback
  const handleDragStart = () => {
    setIsDragActive(true);
  };

  const handleDragEnd = () => {
    setIsDragActive(false);
  };

  const onAssistantClick = (assistant) => {
    // Call the parent's assistant selection handler
    if (props.onAssistantClick) {
      props.onAssistantClick(assistant);
    }
    //console.log("onAssistantClick setShowAssistantDropdown to false");
    setShowAssistantDropdown(false);
  };

  const updateDefaultAssistant = (assistant, e) => {
    // Call the parent's default assistant update handler
    if (props.updateDefaultAssistant) {
      props.updateDefaultAssistant(assistant, e);
    }
  };

  // Calculate dropdown position for portal
  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // 4px margin
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Calculate guideline dropdown position for portal
  const calculateGuidelineDropdownPosition = (buttonElement) => {
    // Target the button-instance-inner element
    const buttonInstanceInner = document.querySelector('.button-instance-inner');
    
    //console.log('Button instance inner found:', buttonInstanceInner);
    
    if (buttonInstanceInner) {
      const rect = buttonInstanceInner.getBoundingClientRect();
      //console.log('Button instance inner rect:', rect);
      //console.log('Window scroll:', window.scrollY, window.scrollX);
      
      const viewportPadding = 8;
      const maxWidth = Math.min(rect.width, window.innerWidth - viewportPadding * 2);
      const clampedLeft = Math.max(
        viewportPadding + window.scrollX,
        Math.min(
          rect.left + window.scrollX,
          window.scrollX + window.innerWidth - maxWidth - viewportPadding
        )
      );
      const position = {
        top: rect.bottom + window.scrollY + 4,
        left: clampedLeft,
        width: maxWidth
      };
      
      //console.log('Calculated position:', position);
      setGuidelineDropdownPosition(position);
      setIsPositionCalculated(true);
    } else if (buttonElement) {
      // Fallback: use button position and estimate position
      const rect = buttonElement.getBoundingClientRect();
      //console.log('Using button fallback, rect:', rect);
      
      const viewportPadding = 8;
      const desiredWidth = Math.max(rect.width * 1.5, 280);
      const maxWidth = Math.min(desiredWidth, window.innerWidth - viewportPadding * 2);
      const clampedLeft = Math.max(
        viewportPadding + window.scrollX,
        Math.min(
          rect.left + window.scrollX,
          window.scrollX + window.innerWidth - maxWidth - viewportPadding
        )
      );
      const position = {
        top: rect.bottom + window.scrollY + 4, // Below the button
        left: clampedLeft,
        width: maxWidth
      };
      
      //console.log('Fallback position:', position);
      setGuidelineDropdownPosition(position);
      setIsPositionCalculated(true);
    } else {
      //console.log('No button-instance-inner or button element found');
    }
  };

  // Static button styles
  const staticButtonsStyles = {
    container: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      justifyContent: 'center',
      marginTop: '10px',
      paddingBottom: '15px',
    },
    button: {
      padding: '8px 16px',
      borderRadius: '20px',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--surface-card)',
      color: 'var(--text-subtle)',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    activeButton: {
    },
    dropdownArrow: {
      fontSize: '12px',
      transition: 'transform 0.2s ease'
    },
    openArrow: {
      transform: 'rotate(180deg)'
    },
    dropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: '0',
      width: '100%', // Use full width of parent
      backgroundColor: 'var(--surface-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '25px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      zIndex: 1000,
      maxHeight: '50vh',
      overflowY: 'auto',
      marginTop: '4px',
      fontFamily: 'Inter, sans-serif'
    },
    dropdownItem: {
      padding: '12px 16px',
      cursor: 'pointer',
    //   borderBottom: '1px solid #f0f0f0',
      fontSize: '13px',
      fontFamily: 'Inter, sans-serif',
      transition: 'background-color 0.2s ease'
    }
  };

  // Static button click handlers
  const handleStaticButtonClick = (button, buttonElement) => {
    if (button.hasDropdown) {
      const isSameCategory = selectedCategory === button.id;
      const nextIsOpen = !(isDropdownOpen && isSameCategory);
      if (nextIsOpen) {
        // Add small delay to ensure DOM is ready
        setTimeout(() => {
          calculateGuidelineDropdownPosition(buttonElement);
        }, 10);
      }
      setIsDropdownOpen(nextIsOpen);
      setSelectedCategory(nextIsOpen ? button.id : null);
      if (!nextIsOpen) {
        setIsPositionCalculated(false);
      }
    } else {
      // Handle direct action buttons
      onSuggestedPromptClick(button.label);
    }
  };

  const handleDropdownItemClick = (prompt) => {
    onSuggestedPromptClick(prompt);
    setIsDropdownOpen(false);
    setSelectedCategory(null);
  };

  const handleClickOutsideDropdown = (event) => {
    if (isDropdownOpen && !event.target.closest('.static-buttons-container') && !guidelineDropdownRef.current?.contains(event.target)) {
      setIsDropdownOpen(false);
      setSelectedCategory(null);
      setIsPositionCalculated(false);
    }
  };

  // Close the plus menu on outside click, and make it keyboard operable:
  // Escape closes and returns focus to the trigger, arrow/Home/End move between
  // items, and focus moves into the menu when it opens.
  useEffect(() => {
    if (!showPlusMenu) return;

    const handleOutside = (e) => {
      if (
        plusMenuRef.current && !plusMenuRef.current.contains(e.target) &&
        plusButtonRef.current && !plusButtonRef.current.contains(e.target)
      ) {
        setShowPlusMenu(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setShowPlusMenu(false);
        plusButtonRef.current?.focus();

        return;
      }

      const menu = plusMenuRef.current;
      if (!menu) {
        return;
      }
      const items = Array.from(menu.querySelectorAll('[role="menuitem"]'));
      if (items.length === 0) {
        return;
      }
      const currentIndex = items.indexOf(document.activeElement);

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = items[(currentIndex + 1) % items.length] || items[0];
        next.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev =
          items[(currentIndex - 1 + items.length) % items.length] ||
          items[items.length - 1];
        prev.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        items[0].focus();
      } else if (e.key === "End") {
        e.preventDefault();
        items[items.length - 1].focus();
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKeyDown);

    // Move focus into the menu once it renders so keyboard users land on the
    // first option.
    const focusFrame = requestAnimationFrame(() => {
      const firstItem = plusMenuRef.current?.querySelector('[role="menuitem"]');
      firstItem?.focus();
    });

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKeyDown);
      cancelAnimationFrame(focusFrame);
    };
  }, [showPlusMenu]);

  const handlePlusClick = () => {
    if (plusButtonRef.current) {
      const rect = plusButtonRef.current.getBoundingClientRect();
      setPlusMenuPosition({
        bottom: window.innerHeight - rect.top + 6,
        left: rect.left,
      });
    }
    setShowPlusMenu((prev) => !prev);
  };

  return (
    <div
      className={`button-instance ${
        props.isHomePage ? "" : "button-instance-padding"
      } ${
        !props.overlayMode &&
        props.hideHotTipContainer &&
        !selectedText &&
        numberOfMessagesInChat === 0
          ? "empty-state-vcenter"
          : ""
      }`}
      style={
        props.overlayMode
          ? {
            //   paddingTop: "3px",
              background: "var(--surface-card)",
              borderRadius: "15px",
              padding: "4px",
            }
          : {}
      }
    >
      <div className="" style={{
        // border: "1px solid rgba(0, 0, 0, 0.6)",
        background: "var(--surface-card)",
        width: "100%",
        borderRadius: "15px",
      }}>
      {props.hideHotTipContainer &&
        !selectedText &&
        numberOfMessagesInChat === 0 && (
          <div className="lightning-instance-parent">
            {
              <div
                className="more-suggestions-title gradient-title"
                style={{
                  textAlign: "center",
                  paddingLeft: "5px",
                  fontSize: "26px",
                  // letterSpacing: "0px",
                  fontWeight: "bold",
                  paddingBottom: "10px",
                }}
              >
                {titleMessage}
              </div>
            }

            {/* {
              <p
                className="add-necessary-bits small-description-text description-helper-text"
                style={{
                  color: "#485165",
                  width: "100%",
                  // paddingBottom: "15px",
                }}
              >
                {descriptionMessage}
              </p>
            } */}
          </div>
        )}

      {/* Assistant Selection Dropdown - ONLY in Overlay Mode */}
      {props.overlayMode && (
        <div className="assistant-selection-container" style={{ marginBottom: "10px", marginLeft: "8px", marginTop: "5px", width: "fit-content", position: "relative"}}>
          <div
            ref={buttonRef}
            className="assistant-selector-button"
            onClick={(e) => {
              e.stopPropagation();
              if (!showAssistantDropdown) {
                calculateDropdownPosition();
              }
              setShowAssistantDropdown(!showAssistantDropdown);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              marginLeft: "5px",
              justifyContent: "space-between",
              padding: "5px 8px",
            //   border: "1px solid #D5D6DB",
              borderRadius: "8px",
              backgroundColor: "var(--surface-card)",
              cursor: "pointer",
              fontSize: "13px",
              color: "var(--text-subtle)",
              width: "fit-content",
              maxWidth: "300px",
            //   boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* {props.selectedAssistant?.imageUrl ? (
                <img
                  src={props.selectedAssistant.imageUrl}
                  alt=""
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    // borderRadius: "50%",
                    // backgroundColor: "#F0F1F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                >
                  <img
                  src={"https://i.imgur.com/BbyEMeR.jpeg"}
                  alt=""
                  style={{
                    width: "20px",
                    height: "20px",
                    // borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
                </div>
              )} */}
              <span style={{ paddingRight: "10px" }}>
                {truncateAssistantName(
                  getWhiteLabeledAssistantName(props.selectedAssistant, props.assistantName),
                  26
                ) || "Select Agent"}
              </span>
            </div>
            <img
              src="/expand.svg"
              alt=""
              style={{
                width: "12px",
                height: "12px",
                transform: showAssistantDropdown ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </div>

          {/* Dropdown Content - Now handled by Portal */}
        </div>
      )}

      <div
        className={`button-instance-container ${
          props.overlayMode ? "" : ""
        }`}
        style={
          overlayMode
            ? {
                background: "var(--surface-card)",
                borderRadius: "15px",
                border: "2px solid transparent",
              }
            : {}
        }
      >
        {props.clarificationPrompt?.options?.length > 0 ? (
          <div className="chat-announcement chat-confirmation-banner" role="status">
            <span className="chat-announcement-title">
              {props.clarificationPrompt.question ||
                "This browser action needs your confirmation."}
            </span>
            <span className="chat-confirmation-actions">
              {props.clarificationPrompt.options.map((option, index) => {
                const label =
                  option?.label || option?.value || `Option ${index + 1}`;
                const value = option?.value || option?.label || label;
                const isConfirm =
                  /^(yes|confirm|proceed|send|approve|ok|sure)/i.test(label);

                return (
                  <button
                    key={`${label}-${index}`}
                    type="button"
                    className={
                      isConfirm
                        ? "input-action-button-2 addy-primary-action chat-confirmation-btn"
                        : "input-action-button-2 chat-confirmation-btn chat-confirmation-btn-secondary"
                    }
                    onClick={() => props.onClarificationChoice?.(value, option)}
                  >
                    {label}
                  </button>
                );
              })}
            </span>
          </div>
        ) : (
          <ChatAnnouncement
            uid={uid}
            email={props.email}
            organizationId={props.organizationId}
            ready={props.organizationResolved}
          />
        )}
        {selectedText?.trim() ? (
          <div className="selected-text-container">
            <div
              className="selected-text-parent"
              style={{
                background: "var(--surface-card)",
                zIndex: 99,
              }}
            >
              <div className="selected-text-header-parent">
                <div className="selected-text-header">{viewTitle}</div>

                <div
                  className="selected-text-close-button"
                  onClick={() => onSelectedTextClose()}
                >
                  <img width="12" height="12" src="/close.svg" />
                </div>
              </div>
              <div className="selected-text">
                {selectedText.length > 250
                  ? selectedText.substring(0, 250) + "..."
                  : selectedText}
              </div>
            </div>

            {numberOfMessagesInChat === 0 && (
              <div className="actions-container">
                <div className="action-title">{selectedType}</div>

                <div className="selected-text-action-div">
                  {selectedTextActions.map((action, index) => (
                    <div
                      onClick={() => onSuggestedPromptClick(action)}
                      key={index}
                      className="suggestion-button selected-text-action"
                    >
                      {action}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : numberOfMessagesInChat === 0 ? (
          !props.hideHotTipContainer && (
            <div className="hot-tip-parent-container">
              <div
                className="selected-text-parent hot-tip-parent"
                id="hot-tip-div"
              >
                <div className="selected-text-header-parent">
                  <div className="selected-text-header">Hot tip!</div>

                  {/* <div className="selected-text-close-button" onClick={() => closeHotTip()}>
                                    <img width="12" height="12" src="/close.svg" />
                                </div> */}
                </div>
                <div className="selected-text">
                  Press and hold the{" "}
                  <mark className="focus-text-mark">`Control` </mark>
                  key, then highlight text on any website to quickly open Addy
                  AI. Try it now!
                </div>
              </div>

              {!props.hideSuggestedPrompts && (
                <div className="lightning-instance-parent">
                  {props.showMoreSuggestionsForYouTitle && (
                    <div className="more-suggestions-title">
                      More suggestions for you
                    </div>
                  )}
                  <div className="lightning-instance tooltip-5">
                    {suggestedPrompts.map((prompt, index) => (
                      <div
                        onClick={() => onSuggestedPromptClick(prompt)}
                        key={index}
                        className="suggestion-button"
                      >
                        {prompt}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          !props.hideSuggestedPrompts && (
            <div className="lightning-instance-parent">
              {props.showMoreSuggestionsForYouTitle && (
                <div className="more-suggestions-title">
                  More suggestions for you
                </div>
              )}
              <div className="lightning-instance tooltip-5" style={{
                width: "100%",
              }}>
                {suggestedPrompts.map((prompt, index) => (
                  <div
                    onClick={() => onSuggestedPromptClick(prompt)}
                    key={index}
                    className="suggestion-button"
                  >
                    {prompt}
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        {shouldShowFollowUps && (
          <div className="lightning-instance-parent follow-up-suggestions-parent">
            <div className="more-suggestions-title follow-up-suggestions-title">
              Suggested follow-ups
            </div>
            <div
              className="lightning-instance tooltip-5 follow-up-suggestions"
              style={{ width: "100%" }}
            >
              {shouldShowFollowUpSkeletons
                ? FOLLOW_UP_SKELETON_IDS.map((skeletonId) => (
                    <div
                      key={`follow-up-skeleton-${skeletonId}`}
                      className="suggestion-button follow-up-suggestion-skeleton"
                      aria-hidden="true"
                    >
                      &nbsp;
                    </div>
                  ))
                : followUpSuggestions.map((prompt, index) => (
                    <button
                      type="button"
                      onClick={() => onSuggestedPromptClick(prompt)}
                      key={`follow-up-${index}`}
                      className="suggestion-button follow-up-suggestion-button"
                    >
                      {prompt}
                    </button>
                  ))}
            </div>
          </div>
        )}

        <div
          className="button-instance-inner tooltip-6"
          style={
            overlayMode
              ? {
                //   background: "white",
                //   boxShadow: "none",
                }
              : {}
          }
        >
          <div
            className="lightning-parent"
            style={{
              display: "flex",
              flexDirection: props.overlayMode ? "row" : "column",
              alignItems: props.overlayMode ? "center" : "start",
              justifyContent: "start",
            }}
          >
            <div className={`flex flex-col ${props.overlayMode ? "gap-[8px]" : "w-full"}`}
            style={{
              width: props.overlayMode ? "65%" : "100%",
            }}>
            {(props?.files?.length > 0 || props?.chatDocs?.length > 0) && (
              <div
                className={
                  "p-2 pb-[56px] w-[100%] align-start justify-start flex flex-row overflow-x-auto"
                }
                style={{
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  gap: "8px",
                }}
              >
                {/* Loan documents picked with "Chat with your docs" (DEV-6130) use the same cards as uploads. */}
                {props?.chatDocs?.length > 0 && (
                  <AttachmentContainer
                    files={toAttachmentFiles(props.chatDocs)}
                    setFiles={(remainingFiles) =>
                      getRemovedChatDocKeys(props.chatDocs, remainingFiles).forEach(
                        (docKey) => props.onRemoveChatDoc(docKey)
                      )
                    }
                  />
                )}
                {props?.files?.length > 0 && (
                  <AttachmentContainer
                    files={props.files}
                    setFiles={props.setFiles}
                  />
                )}
              </div>
            )}
            {queuedExternalQueries.length > 0 && (
              <div className="addy-queued-query-card" role="status" aria-live="polite">
                <div className="addy-queued-query-header">
                  <span className="addy-queued-query-dot" aria-hidden="true" />
                  <span>
                    {queuedExternalQueries.length} question
                    {queuedExternalQueries.length === 1 ? "" : "s"} queued
                  </span>
                </div>
                <div className="addy-queued-query-message">
                  {props.queuedExternalQueryMessage ||
                    "Addy will answer this after the current response."}
                </div>
                {nextQueuedExternalQuery?.displayQuery && (
                  <div className="addy-queued-query-text">
                    "{nextQueuedExternalQuery.displayQuery}"
                  </div>
                )}
              </div>
            )}
            {props.preparingExternalQuery ? (
              <div
                className="addy-prompt-input addy-preparing-query-card"
                style={{
                  maxWidth: props.overlayMode ? "100%" : "100%",
                  minHeight: props.overlayMode ? undefined : "4.6em",
                }}
              >
                <div className="addy-preparing-query-orb" aria-hidden="true">
                  <span />
                </div>
                <div className="addy-preparing-query-content">
                  <div className="addy-preparing-query-status">
                    <span>{props.preparingExternalQuery.status || "Preparing Ask Addy..."}</span>
                    <span className="addy-preparing-query-dots" aria-hidden="true">
                      <i />
                      <i />
                      <i />
                    </span>
                  </div>
                  <div className="addy-preparing-query-text">
                    “{props.preparingExternalQuery.query}”
                  </div>
                  <div className="addy-preparing-query-progress" aria-hidden="true" />
                </div>
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                className={`addy-prompt-input ${isAnimating ? 'animating' : ''}`}
                style={{
                  maxWidth: props.overlayMode ? "100%" : "100%",
                  height: props.overlayMode
                    ? "100%"
                    : `${Math.max(1, Math.min(linesCount, 5)) * 1.45}em`,
                  minHeight: props.overlayMode ? undefined : "1.45em",
                  overflowY: linesCount <= 1 ? "hidden" : "auto",
                  backgroundColor: inputDisabled ? "var(--surface-muted)" : "transparent",
                  cursor: inputDisabled ? "not-allowed" : "text",
                }}
                placeholder={
                  placeholders[currentPlaceholderIndex] || props.placeholder
                }
                value={textareaValue}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                disabled={inputDisabled}
              />
            )}
            {inputDisabled && !props.preparingExternalQuery && (
              <div className="input-lock-banner">
                <span>{props.inputDisabledMessage}</span>
                {typeof props.lockCountdownSeconds === "number" &&
                  props.lockCountdownSeconds > 0 && (
                    <span className="input-lock-countdown">
                      {" "}
                      Try again in {props.lockCountdownSeconds}s
                    </span>
                  )}
              </div>
            )}
            </div>

            <div
              className="input-actions-div flex justify-between items-center w-full"
              style={
                overlayMode
                  ? {
                    //   width: "30%",
                    }
                  : {}
              }
            >
              <div className="input-actions-div-2 flex justify-between items-center w-full gap-2">
                {/* {props.overlayMode && (
                  <Tooltip title="More options" arrow>
                    <div>
                      <IconButton
                        onClick={() => setShowMoreOptions(!showMoreOptions)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </div>
                  </Tooltip>
                )} */}
                <div
                  onClick={() => setShowMoreOptions(!showMoreOptions)}
                  style={
                    overlayMode
                      ? {
                        //   display: showMoreOptions ? "flex" : "none",
                        //   background: "#FFFFFF",
                        //   borderRadius: "10px",
                        //   padding: "10px",
                        //   width: "fit-content",
                        //   height: "fit-content",
                        //   position: "absolute",
                        //   top: "5px",
                        //   right: "165px",
                        //   zIndex: "999999",
                        //   boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                        //   paddingTop: "5px",
                        //   paddingBottom: "5px",
                        display: "flex",
                        flexDirection: "row",
                        }
                      : {
                          display: "flex",
                          flexDirection: "row",
                        }
                  }
                >
                  <div
                    className="flex items-center gap-2 justify-start items-start"
                    style={{ marginRight: "9px" }}
                  >
                    <Tooltip title={attachmentMenuLabel} arrow>
                      <button
                        type="button"
                        ref={plusButtonRef}
                        onClick={handlePlusClick}
                        aria-haspopup="menu"
                        aria-expanded={showPlusMenu}
                        aria-label={attachmentMenuLabel}
                        className="input-action-button inline-flex items-center gap-1"
                        style={{
                          color: showPlusMenu
                            ? "var(--text-brand)"
                            : "var(--text-secondary)",
                          border: showPlusMenu
                            ? "1px solid var(--background-label-color, var(--brand-light))"
                            : "1px solid var(--border-strong)",
                          backgroundColor: showPlusMenu
                            ? "var(--selected-color, var(--brand-muted))"
                            : "transparent",
                          fontWeight: "600",
                          fontSize: "18px",
                          lineHeight: 1,
                          minWidth: "32px",
                          justifyContent: "center",
                          cursor: "pointer",
                          appearance: "none",
                          transition:
                            "color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
                        }}
                      >
                        {/* A "+" rotated 45° reads as an "×", so the open state
                            animates the plus into a colored close icon. */}
                        <span
                          aria-hidden="true"
                          style={{
                            display: "inline-block",
                            transform: showPlusMenu ? "rotate(45deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        >
                          +
                        </span>
                      </button>
                    </Tooltip>
                  </div>
                  {/* Chip for comparison */}
                  {isCompareGuidelinesEnabled && (
                    <Tooltip
                      key={"compare-mode-button"}
                      title={"Compare investor guidelines"}
                      arrow
                    >
                      <div
                        onClick={() => toggleCompareMode(compareMode)}
                        className={`input-action-button inline-flex items-center gap-1 ${
                          compareMode ? "compare-mode-active" : ""
                        }`}
                        style={
                          compareMode
                            ? {
                                color: "var(--text-brand)",
                                border: "1px solid var(--background-label-color)",
                                backgroundColor: "var(--selected-color)",
                              }
                            : {
                                color: "var(--text-secondary)",
                                border: "1px solid var(--border-strong)",
                                backgroundColor: "transparent",
                            }
                        }
                      >
                        <CompareArrowsIcon sx={{ fontSize: 18 }} />
                        Compare
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className="input-actions-div-2 flex justify-between items-center w-full gap-2">
                <Tooltip
                  key={"compare-mode-button"}
                  title={currentlyTyping ? "Stop generating" : "Send message"}
                  arrow
                >
                  <div
                    className="flex items-center gap-2 justify-start items-start"
                    style={{ marginRight: "9px" }}
                  >
                    {currentlyTyping ? (
                      <div
                        onClick={() => props.onStopStreaming?.()}
                        className="input-action-button-2 addy-primary-action inline-flex items-center gap-1"
                        style={{
                          color: "#FFFFFF",
                          border: "1px solid var(--primary-color)",
                          backgroundColor: "var(--primary-color)",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <StopIcon />
                      </div>
                    ) : (
                      <div
                        onClick={() => {
                          if (isButtonEnabled()) {
                            onClick(textareaValue, selectedText, compareMode);
                          }
                        }}
                        className={`input-action-button-2 inline-flex items-center gap-1 ${
                          isButtonEnabled()
                            ? "addy-primary-action"
                            : "addy-primary-action-disabled"
                        }`}
                        style={{
                          color: isButtonEnabled() ? "#FFFFFF" : "var(--text-secondary)",
                          border: isButtonEnabled()
                            ? "1px solid var(--primary-color)"
                            : "1px solid var(--border-strong)",
                          backgroundColor: isButtonEnabled()
                            ? "var(--primary-color)"
                            : "var(--surface-emphasis)",
                          cursor: isButtonEnabled() ? "pointer" : "not-allowed",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <ArrowUpwardIcon />
                      </div>
                    )}
                  </div>
                </Tooltip>

                {props.voiceMode ? (
                  <div
                    onClick={props.onEndVoice}
                    className="voice-end-button inline-flex items-center gap-1"
                    style={{
                      color: "#FFFFFF",
                      border: "none",
                      backgroundColor: "var(--primary-color)",
                      borderRadius: "20px",
                      padding: "6px 14px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 500,
                      gap: "6px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span className="voice-bars">
                      <span className="voice-bar" />
                      <span className="voice-bar" />
                      <span className="voice-bar" />
                      <span className="voice-bar" />
                    </span>
                    End
                  </div>
                ) : (
                  <Tooltip
                    key={"voice-mode-button"}
                    title={"Enter voice mode"}
                    arrow
                  >
                    <div
                      onClick={() => {
                        if (props.recording) {
                          props.stopRecording();
                        } else {
                          props.startRecording();
                        }
                      }}
                      className="input-action-button inline-flex items-center gap-1"
                      style={{
                        color: "var(--text-secondary)",
                        border: "1px solid var(--border-strong)",
                        backgroundColor: "transparent",
                      }}
                    >
                      <GraphicEqIcon />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
        {numberOfMessagesInChat === 0 && !props.hideSuggestedPrompts && (
          <div className="static-buttons-container" style={staticButtonsStyles.container}>
            {staticActionButtons.map((button) => (
              <div key={button.id} style={{ position: 'relative' }}>
                <button
                  style={{
                    ...staticButtonsStyles.button,
                    ...(selectedCategory === button.id && isDropdownOpen ? staticButtonsStyles.activeButton : {})
                  }}
                  onClick={(e) => {
                    const buttonElement = e.currentTarget;
                    handleStaticButtonClick(button, buttonElement);
                  }}
                >
                  <img src={button.svgIconPath} alt="" style={{ width: '16px', height: '16px', }} />
                  {button.label}
                  {button.hasDropdown && ( null
                    // <span style={{
                    //   ...staticButtonsStyles.dropdownArrow,
                    //   ...(isDropdownOpen ? staticButtonsStyles.openArrow : {})
                    // }}>
                    //   ▼
                    // </span>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}

        {!props.hideFeedbackRequestText && !props.overlayMode && (
          <div className="selected-text feedback-request-text">
            <a href="https://forms.gle/z5mJsnJKNGiX7SGt8" target="_blank">
              Give us feedback
            </a>{" "}
            to help improve Addy AI
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/jpg,application/pdf,text/xml,application/xml,application/x-xml"
        style={{ display: "none" }}
        max={100}
        onChange={(e) => {
          if (isOverChatFileLimit(e.target.files.length)) {
            props.toastify(FILE_LIMIT_MESSAGE, false);

            return;
          }
          handleFileChange(e);
        }}
      />
      </div>
      
      {/* Portal for Assistant Dropdown */}
      {showAssistantDropdown && createPortal(
        <div
          ref={assistantDropdownRef}
          style={{
            position: "absolute",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999,
          }}
        >
          <ChatAssistantDropDown2
            overlayMode={props.overlayMode}
            numberOfMessagesInChat={props.numberOfMessagesInChat}
            displayPosition="bottom"
            assistants={props.assistants || []}
            hideAvatar={false}
            selectedAssistant={props.selectedAssistant}
            assistantName={props.assistantName}
            referencedElementId="assistant-selector-button"
            defaultAssistant={props.defaultAssistant}
            organizationDefaultAssistant={props.organizationDefaultAssistant}
            canManageOrganizationDefaultAssistant={
              props.canManageOrganizationDefaultAssistant
            }
            onAssistantClick={onAssistantClick}
            updateDefaultAssistant={updateDefaultAssistant}
            updateOrganizationDefaultAssistant={
              props.updateOrganizationDefaultAssistant
            }
            headerText="Agents"
            createNewLink={GlobalVariables.getEmbeddingAppOrigin()}
            hideTabs={true} // Simplified view for ButtonInstance
            smallContainer={true} // Compact mode
          />
        </div>,
        document.body
      )}

      {/* Portal for Guideline Dropdown */}
      {isDropdownOpen && selectedCategory && isPositionCalculated && createPortal(
        <div
          ref={guidelineDropdownRef}
          style={{
            position: "absolute",
            top: `${guidelineDropdownPosition.top}px`,
            left: `${guidelineDropdownPosition.left}px`,
            width: `${guidelineDropdownPosition.width}px`,
            zIndex: 9999,
          }}
        >
          <div style={staticButtonsStyles.dropdownMenu}>
            {staticActionButtons.find(btn => btn.id === selectedCategory)?.prompts.map((promptItem, index) => {
              const visibleLabel =
                promptItem && typeof promptItem === "object"
                  ? promptItem.label
                  : promptItem;
              return (
                <div
                  key={index}
                  style={staticButtonsStyles.dropdownItem}
                  onClick={() => handleDropdownItemClick(promptItem)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--surface-hover)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--surface-card)'}
                >
                  {visibleLabel}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {/* Portal for + button dropdown menu */}
      {showPlusMenu && createPortal(
        <div
          ref={plusMenuRef}
          role="menu"
          aria-label={attachmentMenuLabel}
          style={{
            position: "fixed",
            bottom: `${plusMenuPosition.bottom}px`,
            left: `${plusMenuPosition.left}px`,
            zIndex: 99999,
            background: "var(--surface-card)",
            border: "1px solid var(--border-default)",
            borderRadius: "14px",
            boxShadow: "0 8px 28px rgba(23, 26, 33, 0.14)",
            minWidth: "252px",
            overflow: "hidden",
            padding: "6px",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {isChatWithDocsEnabled && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setShowPlusMenu(false); props.onChatWithDocs?.(); }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px 12px",
                borderRadius: "10px",
                cursor: "pointer",
                width: "100%",
                textAlign: "left",
                background: "var(--surface-card)",
                border: "none",
                appearance: "none",
                font: "inherit",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
              onFocus={(e) => {
                if (isKeyboardFocus(e.currentTarget)) {
                  e.currentTarget.style.background = "var(--surface-hover)";
                }
              }}
              onBlur={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
            >
              <span style={{
                width: 34, height: 34, borderRadius: 9,
                background: "var(--surface-muted)",
                color: "var(--text-secondary)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M4 7a2 2 0 012-2h3l2 2h7a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                </svg>
              </span>
              <span style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "13.5px", fontWeight: 600, color: "var(--text-primary)" }}>Chat with your docs</span>
                <span style={{ fontSize: "11.5px", color: "var(--text-muted)", marginTop: "1px" }}>Pick a loan and its documents</span>
              </span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => { setShowPlusMenu(false); handleFiles(); }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "10px 12px",
              borderRadius: "10px",
              cursor: "pointer",
              width: "100%",
              textAlign: "left",
              background: "var(--surface-card)",
              border: "none",
              appearance: "none",
              font: "inherit",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
            onFocus={(e) => {
                if (isKeyboardFocus(e.currentTarget)) {
                  e.currentTarget.style.background = "var(--surface-hover)";
                }
              }}
            onBlur={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
          >
            <span style={{
              width: 34, height: 34, borderRadius: 9,
              background: "var(--surface-muted)", color: "var(--text-secondary)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <img src="/attachFile.svg" alt="" style={{ width: 15, height: 15, opacity: 0.65 }} />
            </span>
            <span style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: "13.5px", fontWeight: 600, color: "var(--text-primary)" }}>Attach files</span>
              <span style={{ fontSize: "11.5px", color: "var(--text-muted)", marginTop: "1px" }}>Upload from your device</span>
            </span>
          </button>
        </div>,
        document.body
      )}

      {/* UploadEverywhere Component for global drag and drop */}
      <UploadEverywhere
        onFileUpload={handleDragDropUpload}
        acceptedFileTypes={['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'text/xml', 'application/xml', 'application/x-xml']}
        maxFileSize={50 * 1024 * 1024} // 50MB
        disabled={currentlyTyping || inputDisabled || (props.files?.length >= 5)}
        multiple={true}
        isUploading={props.files?.some(file => file.loading)}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        overlayTitle="Drop Files to Upload"
        overlayDescription="PDF, PNG, JPG, XML files up to 50MB each • Upload starts immediately"
        overlayMode={props.overlayMode}
      />
    </div>
  );
};

export default ButtonInstance;
